import { memo, ReactElement, Suspense, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import LS from 'utils/bom-dom-manipulation/local-storage';
import { ON_ACCEPT_BET_TYPES } from 'utils/constants/betslip/bet-types';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { EBetslipPage } from 'interfaces/bet-data';
import { EBetslipType, TabObject } from 'interfaces/betslip';
import { useBetslipConfigsContext } from 'newelements/_Betslip/providers/configs-provider';
import { useHeaderVisibility } from 'newhooks/generic/useHeaderVisibility';
import {
  setActiveTabPage,
  setBetslipTabs,
  setSelectedBetType,
  setSelectedStake as updateSelectedStake
} from 'store/actions';
import {
  getAllLsBets,
  getOpenBetsCount,
  getTabs
} from 'store/selectors/betslip';

const { BetslipSetupManagerMobile } = lazy(
  () =>
    import(
      /* webpackChunkName: "betslip-setup-manager-basic-mobile" */ 'newelements/_Betslip/managers/SetupManager/Mobile'
    )
);

export const BetslipSetupManager = memo((): ReactElement | null => {
  const {
    computed: { betslipType }
  } = useBetslipConfigsContext();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tabs = useSelector(getTabs);
  const openBetsCount = useSelector(getOpenBetsCount);
  const allBets = useSelector(getAllLsBets);
  const allBetsCount = allBets.length;

  useHeaderVisibility();
  const betslipTab = useMemo(() => {
    return {
      id: 0,
      title: t('betslip.betslip'),
      badge: allBetsCount
    };
  }, [allBetsCount]);

  const openBetsTab: TabObject = useMemo(() => {
    return {
      id: 1,
      title: t('betslip.openBets'),
      badge: openBetsCount
    };
  }, [openBetsCount]);

  useLayoutEffect(() => {
    !tabs && dispatch(setBetslipTabs([betslipTab]));
  }, []);

  useLayoutEffect(() => {
    const onBlur = () => {
      dispatch(updateSelectedStake(''));

      if (betslipType === EBetslipType.ADVANCED) {
        dispatch(setSelectedBetType(null));
      }
    };

    window.addEventListener('blur', onBlur);

    if (!JSON.parse(LS.getItem(storageKeyName('betslip', 'ACCEPT_BET')))) {
      const oddAcceptType = SpringConfigs.DEFAULT_ODD_ACCEPT
        ? ON_ACCEPT_BET_TYPES.find(elem => {
            return elem.value === SpringConfigs.DEFAULT_ODD_ACCEPT;
          })
        : ON_ACCEPT_BET_TYPES[0];

      LS.setItem(
        storageKeyName('betslip', 'ACCEPT_BET'),
        JSON.stringify(oddAcceptType)
      );
    }

    return () => {
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // @Todo need to move "page" and "tab" logic and their changing logic into config provider
  useLayoutEffect(() => {
    if (openBetsCount) {
      dispatch(setBetslipTabs([betslipTab, openBetsTab]));
    } else {
      dispatch(setBetslipTabs([betslipTab]));
      dispatch(setActiveTabPage(EBetslipPage.BETSLIP));
    }
  }, [openBetsCount, allBetsCount]);

  // useEffect(() => {
  //   const isMultiples = betType !== EBetslipBetType.SINGLE;
  //
  //   console.log('calculateAndDispatchUpdates', isMultiples);
  //
  //   if (isMultiples) {
  //     // alert('calculate');
  //     calculateAndDispatchUpdates({ id: GENERAL_FIELD_KEY, stake: '' });
  //   }
  // }, []);

  return isMobile() ? (
    <Suspense>
      <BetslipSetupManagerMobile />
    </Suspense>
  ) : null;
});
